import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import type { CatalogProduct, CategoryFilter, SortOption } from 'shared';
import { ProductCatalogService } from 'shared';
import { CartService } from '../../services/cart.service';

@Component({
  selector: 'app-catalog',
  templateUrl: './catalog.component.html',
  styleUrl: './catalog.component.scss'
})
export class CatalogComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly catalog = inject(ProductCatalogService);
  private readonly cart = inject(CartService);

  readonly categoriaFromUrl = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('categoria'))),
    { initialValue: this.route.snapshot.queryParamMap.get('categoria') }
  );

  readonly pageSize = 12;

  readonly sortOptions: { value: SortOption; label: string }[] = [
    { value: 'popular', label: 'Más populares' },
    { value: 'priceAsc', label: 'Precio: menor a mayor' },
    { value: 'priceDesc', label: 'Precio: mayor a menor' }
  ];

  readonly filter = signal<CategoryFilter>('all');
  readonly sort = signal<SortOption>('popular');
  readonly page = signal(1);
  readonly sortOpen = signal(false);
  private readonly _addedIds = signal<Set<string>>(new Set());

  constructor() {
    effect(() => {
      const c = this.categoriaFromUrl();
      if (c === 'recamaras' || c === 'muebles') {
        this.filter.set(c);
        this.page.set(1);
      }
    });

    effect(() => {
      const max = this.totalPages();
      if (this.page() > max) {
        this.page.set(max);
      }
    });
  }

  readonly filteredSorted = computed(() => {
    let list: CatalogProduct[] = [...this.catalog.products()];
    const f = this.filter();
    if (f !== 'all') {
      list = list.filter((p) => p.category === f);
    }
    const s = this.sort();
    if (s === 'priceAsc') {
      list.sort((a, b) => a.priceValue - b.priceValue);
    } else if (s === 'priceDesc') {
      list.sort((a, b) => b.priceValue - a.priceValue);
    }
    return list;
  });

  readonly totalPages = computed(() => {
    const n = this.filteredSorted().length;
    return Math.max(1, Math.ceil(n / this.pageSize));
  });

  readonly pageNumbersList = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1)
  );

  readonly pageItems = computed(() => {
    const list = this.filteredSorted();
    const p = this.page();
    const start = (p - 1) * this.pageSize;
    return list.slice(start, start + this.pageSize);
  });

  readonly sortLabel = computed(() => {
    return this.sortOptions.find((o) => o.value === this.sort())?.label ?? 'Más populares';
  });

  setFilter(f: CategoryFilter): void {
    this.filter.set(f);
    this.page.set(1);
  }

  setSort(s: SortOption): void {
    this.sort.set(s);
    this.sortOpen.set(false);
    this.page.set(1);
  }

  toggleSort(): void {
    this.sortOpen.update((v) => !v);
  }

  closeSort(): void {
    this.sortOpen.set(false);
  }

  goPage(p: number): void {
    const max = this.totalPages();
    if (p >= 1 && p <= max) {
      this.page.set(p);
    }
  }

  nextPage(): void {
    const max = this.totalPages();
    if (this.page() < max) {
      this.page.update((n) => n + 1);
    }
  }

  isAdded(productId: string): boolean {
    return this._addedIds().has(productId);
  }

  addToCart(product: CatalogProduct, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.cart.add({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      description: product.lead ?? product.category,
      image: product.image,
      unitPrice: product.priceValue,
      qty: 1
    });
    this._addedIds.update((ids) => new Set(ids).add(product.id));
    window.setTimeout(() => {
      this._addedIds.update((ids) => {
        const next = new Set(ids);
        next.delete(product.id);
        return next;
      });
    }, 2000);
  }
}
